import { useEffect, useState } from 'react';
import { formatNumber } from 'utils/utils';
import { Button } from '../Button';
import Loader from 'components/Loader';
import RadioButton from '../RadioButton';
import TextBox from '../TextBox';
import ShoppingCartService from '../services/shopping-cart.service';
import { Headings } from '../Headings';
import { useErrorBoundary } from 'basket/ErrorBoundary';
import FabricIcon from 'components/FabricIcon';
import { SupplyOwnFabric } from '../SupplyOwnFabric';
import ProductQtyInput from 'components/ProductQtyInput';
import { OtherFabricSelector } from '../OtherFabricSelector';

export function BasketEditValancesItem(props) {
    const { item, updateFabricItem, close } = props;
    const handleError = useErrorBoundary();
    const [quantity, setQuantity] = useState(item.quantity);
    const [price, setPrice] = useState(item.price);
    const convertToBoolean = (value) => value == "1"? 1 : (value == "0"? 0 : value);
    const [fabricData, setFabricData] = useState({
        headingId: item.fabric.headingId,
        unitOfMeasurement: item.fabric.unitOfMeasurement,
        width: item.fabric.width,
        eyeDrop: item.fabric.eyeDrop,
        eyeletId: item.fabric.eyeletId,
        headingHeight: item.fabric.headingHeight,
        liningId: item.fabric.liningId,
        liningColour: item.fabric.liningColour,
        interlining: item.fabric.interlining,
        handSewn: item.fabric.handSewn,
        customisationRequests: item.fabric.customisationRequests,
        windowLocation: item.fabric.windowLocation,
        fabricId: item.fabricId,
        supplyOwnFabricNameOrDescription: item.supplyOwnFabricNameOrDescription,
        supplyOwnFabricRollWidth: item.supplyOwnFabricRollWidth,
        supplyOwnFabricPatternRepeat: item.supplyOwnFabricPatternRepeat
    });
    const [loading, setLoading] = useState(false);

    const [itemTitle, setItemTitle] = useState(`${item.fabric.fabricName} ${item.colour || ''} Fabric By ${item.fabric.supplierName || ''}`);
    const title = () => `Edit Valances In ${itemTitle}`;

    const [headingsSmallView, setHeadingsSmallView] = useState([]);
    const [headingsAll, setHeadingsAll] = useState([]);
    const [linings, setLinings] = useState([]);
    const [eyelets, setEyelets] = useState([]);

    const [headingHeightMin, setHeadingHeightMin] = useState(null);
    const [headingHeightMax, setHeadingHeightMax] = useState(null);
    
    const [errors, setErrors] = useState({});
    const [isSubmitDisabled, setSubmitDisabled] = useState(false);

    const [fabrics, setFabrics] = useState([])
    const [totalFabricsCount, setTotalFabricsCount] = useState(0)
    const [selectedFabric, setSelectedFabric] = useState(fabricData.fabricId)

    const fetchOptions = async () => {
        const optionsResponse = await ShoppingCartService.create('options/8',{})
            .catch(error => { handleError('Could not load item details. Please contact administrator'); });
        if (optionsResponse){                
            const { headingsAll, linings, eyelets } = optionsResponse;        
            setHeadingsAll(headingsAll);
            setLinings(linings);
            setEyelets(eyelets);
        }

        const { headingsSmallView, headingHeightLimitations } = await ShoppingCartService.create('options/1', {
            option: "headingsSmallView",
            headingId: fabricData.headingId
        }).catch(error => { handleError('Could not load item details. Please contact administrator'); });
        if (headingsSmallView){                
            setHeadingsSmallView(headingsSmallView);
        }
        if (headingHeightLimitations){
            setHeadingHeightMin(headingHeightLimitations.headingHeightMin);
            setHeadingHeightMax(headingHeightLimitations.headingHeightMax);
        }
        else {
            setHeadingHeightMin(null);
            setHeadingHeightMax(null);
        }
    };

    const handleQuantityChange = (value) => {
        if (value > 0 && value <= 100) {
            setSubmitDisabled(false)
            setQuantity(value)
        }
        else {
            setSubmitDisabled(true)
        }
    };

    const handleInputChange = (e) => {        
        const { name, value } = e.target;
        const updatedData = { ...fabricData, [name]: value };
        setFabricData(updatedData);

        const validationError = validateInput(name, value);
        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: validationError
        }));
    };

    const setHeadingId = (headingId) => {
        const updatedData = { ...fabricData, headingId: headingId };
        setFabricData(updatedData);
    };

    useEffect(() => {
        if (headingHeightMin == null || headingHeightMin == undefined || headingHeightMax == null || headingHeightMax == undefined){
            setErrors((prevErrors) => {
                const { headingHeight, ...rest } = prevErrors;
                return rest;
            });
        }

        const validationError = validateInput('headingHeight', fabricData.headingHeight);
        if (validationError){                    
            const updatedData = { ...fabricData, headingHeight: 0 };
            setFabricData(updatedData);
        }
    }, [headingHeightMax, headingHeightMin]);

    useEffect(() => {
        const hasErrors = Object.values({ ...errors }).some((error) => error);
        setSubmitDisabled(hasErrors);
    }, [errors]);

    const validateInput = (name, value) => {
        let error = '';
        const num = Number(value);
        const errorMessage = "Invalid data";

        if (name === 'width') {
            if (!Number.isInteger(num) || num < 30 || num > 1000) {
                error = errorMessage;
            }
        } else if (name === 'eyeDrop') {
            if (!Number.isInteger(num) || num < 5 || num > 120) {
                error = errorMessage;
            }
        }
        else if (name === 'headingHeight'){
            if ((headingHeightMin !== null && headingHeightMin !== undefined) &&  (headingHeightMax !== null && headingHeightMax !== undefined) && 
            (!Number.isInteger(num) || num < headingHeightMin || num > headingHeightMax)) {
                error = errorMessage;
            }
        } else if (name === 'supplyOwnFabricPatternRepeat' || name === 'supplyOwnFabricRollWidth') {
            if (!Number.isInteger(num) || num <= 0 || num > 999) {
                error = errorMessage;
            }
        } else if (name === 'supplyOwnFabricNameOrDescription') {
            if (!value || value.trim() === '') {
                error = "This field is required";
            }
        }
        return error;
    };

    const handleUpdate = () => {
        setLoading(true);
        updateFabricItem({
            ...item,
            quantity,
            fabricId: selectedFabric,
            fabric: { 
                ...fabricData,
                unitOfMeasurement: convertToBoolean(fabricData.unitOfMeasurement),
                liningColour: convertToBoolean(fabricData.liningColour),
                interlining: convertToBoolean(fabricData.interlining),
                handSewn: convertToBoolean(fabricData.handSewn),
                liningId: parseInt(fabricData.liningId),
            },
            supplyOwnFabricNameOrDescription: fabricData.supplyOwnFabricNameOrDescription,
            supplyOwnFabricPatternRepeat: fabricData.supplyOwnFabricPatternRepeat,
            supplyOwnFabricRollWidth: fabricData.supplyOwnFabricRollWidth
        });
        setLoading(false);
    };

    const updatePrice = async () => {
        const result = await ShoppingCartService.create('price', {
            ...item,
            quantity,
            fabricId: selectedFabric,
            fabric: { 
                ...fabricData,
                fabricId: selectedFabric,
                unitOfMeasurement: convertToBoolean(fabricData.unitOfMeasurement),
                liningColour: convertToBoolean(fabricData.liningColour),
                interlining: convertToBoolean(fabricData.interlining),
                handSewn: convertToBoolean(fabricData.handSewn),
                liningId: parseInt(fabricData.liningId),
            },
            supplyOwnFabricNameOrDescription: fabricData.supplyOwnFabricNameOrDescription,
            supplyOwnFabricPatternRepeat: fabricData.supplyOwnFabricPatternRepeat,
            supplyOwnFabricRollWidth: fabricData.supplyOwnFabricRollWidth
        }).catch(error => { handleError('Could not load item price. Please contact administrator'); });
        if (result) setPrice(result.price);
    }

    useEffect(() => {
        if (!isSubmitDisabled) updatePrice();
    }, [fabricData]);

    const [isHookVisible, setIsHookVisible] = useState(false);
    useEffect(() => {
        let hideIds = [4, 5, 8, 12, 13, 14, 15, 22];
        setIsHookVisible(!hideIds.includes(fabricData.headingId));
    }, [fabricData.headingId]);

    const [isEyeletsVisible, setIsEyeletsVisible] = useState(false);
    useEffect(() => {
        let hideIds = [4, 12, 13, 14];
        setIsEyeletsVisible(hideIds.includes(fabricData.headingId));
    }, [fabricData.headingId]);

    const [eyeletSize, setEyeletSize] = useState("");
    const setEyeletId = (eyeletId) => {
        const updatedData = { ...fabricData, eyeletId: eyeletId };
        setFabricData(updatedData);
    };

    useEffect(() => console.log(item), [])

    const loadFabrics = async () => {
        setLoading(true)
        const response = await ShoppingCartService.get('same-fabrics?fabricId=' + fabricData.fabricId)
        setFabrics(response.fabrics)
        setTotalFabricsCount(response.totalCount)
        setLoading(false)
    }

    const loadAllFabrics = async () => {
        setLoading(true)
        const response = await ShoppingCartService.get('same-fabrics?fabricId=' + fabricData.fabricId + '&showAll=true')
        setFabrics(response.fabrics)
        setTotalFabricsCount(response.totalCount)
        setLoading(false)
    }

    useEffect(() => {
        const foundFabric = fabrics.find(fabric => fabric.fabricId === selectedFabric);
        if (foundFabric){                    
            setItemTitle(foundFabric.fullName);
            updatePrice();
        }
    }, [selectedFabric]);

    useEffect(() => {
        fetchOptions()
        loadFabrics()
    }, []);

    return (
        <Loader isLoading={loading}>
            <form className="p-32 pb-54 bg3 measurements-change active-group sm-p-20">
                <div className="row">
                    <div className="col">
                        <div className="subtitle1 mb-16 sm-text-24">{title()}</div>
                    </div>
                    <div className="col-auto">
                        <span
                            className="curtains-icon curtains-icon-close size-24 show-btn hide-btn"
                            onClick={() => close()}
                        ></span>
                    </div>
                </div>

                {
                    item.supplyOwnFabric && (
                        <SupplyOwnFabric fabricData={fabricData} setFabricData={setFabricData} errors={errors} handleInputChange={handleInputChange} />
                    )
                }

                <OtherFabricSelector fabrics={fabrics} totalFabricsCount={totalFabricsCount} loadAllFabrics={loadAllFabrics} selectedFabric={selectedFabric} setSelectedFabric={setSelectedFabric} />

                <Headings
                    headingsSmallView={headingsSmallView} 
                    headingsAll={headingsAll}
                    headingId={fabricData.headingId}
                    setHeadingId={setHeadingId} 
                    setEyeletSize={setEyeletSize} 
                    price={price*quantity}
                    setHeadingHeightMin = {setHeadingHeightMin}
                    setHeadingHeightMax = {setHeadingHeightMax}
                />

                {/* Measurements */}
                <div>
                    <div className="body-text3 gray mb-8">Measurements:</div>
                    <div className="d-flex gap-32 mb-16">
                        <RadioButton
                            name="unitOfMeasurement"
                            label={'Cm'}
                            value={0}
                            checked={fabricData.unitOfMeasurement == 0}
                            onChange={handleInputChange}
                        />
                        <RadioButton
                            name="unitOfMeasurement"
                            label={'Inches'}
                            value={1}
                            checked={fabricData.unitOfMeasurement == 1}
                            onChange={handleInputChange}
                        />
                    </div>

                    <div className="d-flex gap-40 sm-gap-16">
                        <div className="max-width-215">
                            <div className="body-text3 mb-8">Width</div>
                            <TextBox
                                name="width"
                                value={fabricData.width}
                                onChange={handleInputChange}
                                unit={fabricData.unitOfMeasurement==0? 'cm':'inches'}
                            />
                            {errors.width && (
                                <span className="field-validation-error">{errors.width}</span>
                            )}
                        </div>
                        <div className="max-width-215">
                            <div className="body-text3 mb-8">Drop</div>
                            <TextBox
                                name="eyeDrop"
                                value={fabricData.eyeDrop}
                                onChange={handleInputChange}
                                unit={fabricData.unitOfMeasurement==0? 'cm':'inches'}
                            />
                            {errors.eyeDrop && (
                                <span className="field-validation-error">{errors.eyeDrop}</span>
                            )}
                        </div>
                    </div>
                </div>
                <hr className="secondary-line mb-16"></hr>

                {/* Hook Position */}
                {isHookVisible && (
                    <div>
                        <div className="body-text3 gray mt-16 mb-8 align-center gap_3">Hook Position: <span className="curtains-icon curtains-icon-Info-circle size-26"></span></div>
                        <div className="max-width-215">                    
                            <div className="body-text3 mb-8">Distance</div>
                            <TextBox
                                name="headingHeight"
                                value={fabricData.headingHeight}
                                onChange={handleInputChange}
                                unit={fabricData.unitOfMeasurement==0? 'cm':'inches'}
                            />
                            {errors.headingHeight && (
                                <span className="field-validation-error">{errors.headingHeight}</span>
                            )}
                        </div>
                        <div className="dashed-line mt-16"></div>
                    </div>
                )}

                {/* Eyelets */}
                {isEyeletsVisible && (
                    <div>
                        <div className="body-text3 gray mt-16 mb-16 align-center gap_3">Select Eyelet Color ({eyeletSize}): <span className="curtains-icon curtains-icon-Info-circle size-26"></span></div>
                            <div className="thumbnails thumbnails-5 have-first-el-active mb-16 active-group row-gap-16">
                                {eyelets && eyelets.length > 0 && eyelets.map((eyelet) => (
                                    eyelet && (
                                        <div className={eyelet.id==fabricData.eyeletId? "active" : ""} key={'eyeletId'+eyelet.id} onClick={() => setEyeletId(eyelet.id)}>
                                            <div className="thumbnail pointer mb_5 rel">
                                                <img src={eyelet.imageUrl}/>
                                                <span className="curtains-icon curtains-icon-Check"></span>
                                            </div>
                                            <div className="body-text6 ps_5">{eyelet.name}</div>
                                        </div>
                                    )
                                ))}
                            </div>
                            <hr className="secondary-line"/>
                    </div>
                )}

                {/* Lining */}
                <div>
                    <div className="body-text3 gray mt-16 mb-8">Lining:</div>
                    <div className="d-flex gap-32 mb-16">
                        {linings && linings.length > 0 && linings.map((lining) => (
                            lining && (
                                <RadioButton
                                key={'liningId'+lining.id}
                                name="liningId"
                                label={lining.name}
                                value={lining.id}
                                checked={fabricData.liningId == lining.id}
                                onChange={handleInputChange}
                            />
                            )
                        ))}
                    </div>
                </div>
                <hr className="secondary-line"/>

                {/* Lining Colour */}
                <div>
                    <div className="body-text3 gray mt-16 mb-16">Lining Colour:</div>
                    <div className="d-flex gap-32 mb-16">
                        <RadioButton
                            name="liningColour"
                            label={'Ivory'}
                            value={0}
                            checked={fabricData.liningColour == 0}
                            onChange={handleInputChange}
                        />
                        <RadioButton
                            name="liningColour"
                            label={'White'}
                            value={1}
                            checked={fabricData.liningColour == 1}
                            onChange={handleInputChange}
                        />
                    </div>
                </div>
                <hr className="secondary-line mb-16"/>

                {/* Interlining */}
                <div>
                    <div className="body-text3 gray mt-16 mb-8">Interlining:</div>
                    <div className="d-flex gap-32 mb-16">
                        <RadioButton
                            name="interlining"
                            label={'Yes'}
                            value={1}
                            checked={fabricData.interlining == 1}
                            onChange={handleInputChange}
                        />
                        <RadioButton
                            name="interlining"
                            label={'No'}
                            value={0}
                            checked={fabricData.interlining == 0}
                            onChange={handleInputChange}
                        />
                    </div>
                </div>
                <div className="dashed-line"></div>

                {/* Handsewn */}
                <div>
                    <div className="body-text3 gray mt-16 mb-8">Handsewn:</div>
                    <div className="d-flex gap-32 mb-16">
                        <RadioButton
                            name="handSewn"
                            label={'Yes'}
                            value={1}
                            checked={fabricData.handSewn == 1}
                            onChange={handleInputChange}
                        />
                        <RadioButton
                            name="handSewn"
                            label={'No'}
                            value={0}
                            checked={fabricData.handSewn == 0}
                            onChange={handleInputChange}
                        />
                    </div>
                </div>
                <div className="dashed-line"></div>

                {/* Customization */}
                <div>
                    <div className="body-text3 gray mt-16 mb-8 align-center gap_3">Curtain Valance Customisation:</div>
                    <div className="max-width-410 mb-16 sm-text-14">If you have special requests, write below or <a href="/contact-us" className="underline">contact us</a> <a href="tel:0345 8620743" className="underline">0345 8620743</a></div>
                    <TextBox
                        name="customisationRequests"
                        value={fabricData.customisationRequests}
                        onChange={handleInputChange}
                        placeholder='e.g. Trim or borders'
                    />
                </div>
                <div className="dashed-line mt-16"></div>

                {/* Window Location */}
                <div>
                    <div className="body-text3 gray mt-16 mb-8 align-center gap_3">Window location:</div>
                    <div className="max-width-410 mb-16 sm-text-14">Let us know where you will be hanging your Curtain Valance, and we will include this in your order details for you. (Optional).</div>
                    <TextBox
                        name="windowLocation"
                        value={fabricData.windowLocation}
                        onChange={handleInputChange}
                        placeholder='e.g. Dining room large window'
                    />
                </div>
                <div className="dashed-line mt-16"></div>

                {/* TOTAL */}
                <div>
                    <div className="subtitle2 mb-8 mt-16">TOTAL: <span className="body-text1">£{formatNumber(price*quantity, 2)}</span></div>
                    <div className="body-text6">Delivery for made-to-measure curtain valances: 2-4 weeks.</div>
                </div>

                <div className="mb-16 mt-32">
                    <div className="dashed-line mb-24"></div>
                    <div className="body-text3 gray mb-16">Quantity:</div>
                    
                    <ProductQtyInput initValue={quantity} onlyInt={true} onChange={handleQuantityChange} />
                </div>

                <div className="d-flex gap-16 mb-16 sm-column sm-gap-8 sm-mb-0">
                    <div className="w-100 show-btn hide-btn">
                        <Button name="UPDATE" onClick={handleUpdate} disabled={isSubmitDisabled} />
                    </div>
                </div>
            </form>
        </Loader>
    );
}